import { levenbergMarquardt } from "ml-levenberg-marquardt";
import { Vector2 } from "three";
import { CYLINDER_FIT_CONFIG } from "./cylinder-fit";

export type CircleFit = { center: Vector2; radius: number };

export type RobustCircleFit = CircleFit & {
  inlierIndices: number[];
  angularCoverage: number;
  radiusReliable: boolean;
};

/**
 * @param points Cross-section samples paired with projected surface normals.
 * @param normals Corresponding normals; both orientations are considered.
 * @param radiusEstimate Provisional radius used only to bound voting.
 * @param cellSize Center-vote resolution from spacing/noise.
 * @param standardRadii Optional nominal outer radii; never used implicitly.
 * @returns Supported normal-vote radius/center, or undefined for ambiguous evidence.
 */
export function fitCircleNormalVotes(
  points: readonly Vector2[],
  normals: readonly Vector2[],
  radiusEstimate: number,
  cellSize: number,
  standardRadii?: readonly number[],
): CircleFit | undefined {
  if (
    points.length !== normals.length ||
    points.length < CYLINDER_FIT_CONFIG.minimumSamples ||
    !(radiusEstimate > 0) ||
    !(cellSize > 0)
  ) {
    return;
  }
  const radii =
    standardRadii ??
    Array.from(
      { length: CYLINDER_FIT_CONFIG.centerVoteRadii },
      (_, index) =>
        radiusEstimate *
        CYLINDER_FIT_CONFIG.centerVoteMinimumRatio *
        (CYLINDER_FIT_CONFIG.centerVoteMaximumRatio /
          CYLINDER_FIT_CONFIG.centerVoteMinimumRatio) **
          (index / (CYLINDER_FIT_CONFIG.centerVoteRadii - 1)),
    );
  let best: CircleFit | undefined;
  let bestCount = 0;
  let bestSpread = Infinity;
  for (const radius of radii) {
    if (!Number.isFinite(radius) || radius <= 0) continue;
    const cells = new Map<string, Vector2[]>();
    for (let index = 0; index < points.length; index++) {
      if (normals[index].lengthSq() <= CYLINDER_FIT_CONFIG.numericalFloor) {
        continue;
      }
      const normal = normals[index].clone().normalize();
      for (const sign of [-1, 1]) {
        const center = points[index]
          .clone()
          .addScaledVector(normal, sign * radius);
        const key = `${Math.round(center.x / cellSize)},${Math.round(center.y / cellSize)}`;
        const bucket = cells.get(key) ?? [];
        bucket.push(center);
        cells.set(key, bucket);
      }
    }
    for (const votes of cells.values()) {
      if (votes.length < points.length * CYLINDER_FIT_CONFIG.minimumConsensus) {
        continue;
      }
      const center = votes
        .reduce((sum, vote) => sum.add(vote), new Vector2())
        .divideScalar(votes.length);
      const spread =
        votes.reduce((sum, vote) => sum + vote.distanceToSquared(center), 0) /
        votes.length;
      if (
        votes.length > bestCount ||
        (votes.length === bestCount && spread < bestSpread)
      ) {
        best = { center, radius };
        bestCount = votes.length;
        bestSpread = spread;
      }
    }
  }
  return best;
}

/**
 * @param points Observed cross-section samples.
 * @param spacing Local measured spacing.
 * @param noise Estimated positional noise.
 * @returns Robust initialized/refined circle and explicit angular-support quality.
 */
export function fitCircleRobust(
  points: readonly Vector2[],
  spacing: number,
  noise: number,
): RobustCircleFit | undefined {
  if (points.length < 3 || !(spacing > 0) || !(noise >= 0)) return;
  const threshold = Math.max(2 * spacing, 3 * noise);
  let state = 42;
  function next(): number {
    state =
      (Math.imul(state, CYLINDER_FIT_CONFIG.randomMultiplier) +
        CYLINDER_FIT_CONFIG.randomIncrement) >>>
      0;
    return Math.floor(
      (state / CYLINDER_FIT_CONFIG.randomDivisor) * points.length,
    );
  }
  let best: number[] = [];
  let bestError = Infinity;
  for (
    let iteration = 0;
    iteration < CYLINDER_FIT_CONFIG.ransacIterations;
    iteration++
  ) {
    const indices = [next(), next(), next()];
    if (new Set(indices).size !== 3) continue;
    const candidate = fitCircleTaubin(indices.map((index) => points[index]));
    if (!candidate) continue;
    const inliers: number[] = [];
    let error = 0;
    for (let index = 0; index < points.length; index++) {
      const residual = Math.abs(
        points[index].distanceTo(candidate.center) - candidate.radius,
      );
      if (residual <= threshold) {
        inliers.push(index);
        error += residual * residual;
      }
    }
    if (
      inliers.length > best.length ||
      (inliers.length === best.length && error < bestError)
    ) {
      best = inliers;
      bestError = error;
    }
  }
  if (best.length < points.length * CYLINDER_FIT_CONFIG.minimumConsensus) {
    return;
  }
  const fitted = fitCircleGeometric(best.map((index) => points[index]));
  if (!fitted) return;
  const inlierIndices = points.flatMap((point, index) =>
    Math.abs(point.distanceTo(fitted.center) - fitted.radius) <= threshold
      ? [index]
      : [],
  );
  const bins = new Set(
    inlierIndices.map((index) => {
      const offset = points[index].clone().sub(fitted.center);
      return (
        Math.floor(
          ((Math.atan2(offset.y, offset.x) + Math.PI) / (2 * Math.PI)) *
            CYLINDER_FIT_CONFIG.angularBins,
        ) % CYLINDER_FIT_CONFIG.angularBins
      );
    }),
  );
  const angularCoverage = (bins.size / CYLINDER_FIT_CONFIG.angularBins) * 360;
  return {
    ...fitted,
    inlierIndices,
    angularCoverage,
    radiusReliable: angularCoverage >= CYLINDER_FIT_CONFIG.minimumArcForRadius,
  };
}

/**
 * @param points Observed cross-section samples.
 * @returns A normalized Taubin circle initialization, or undefined for a degenerate arc.
 */
export function fitCircleTaubin(
  points: readonly Vector2[],
): CircleFit | undefined {
  if (
    points.length < 3 ||
    points.some(
      (point) => !Number.isFinite(point.x) || !Number.isFinite(point.y),
    )
  ) {
    return;
  }
  const mean = points
    .reduce((sum, point) => sum.add(point), new Vector2())
    .divideScalar(points.length);
  const scale = Math.sqrt(
    points.reduce((sum, point) => sum + point.distanceToSquared(mean), 0) /
      points.length,
  );
  if (!(scale > CYLINDER_FIT_CONFIG.numericalFloor)) return;
  let squareX = 0;
  let squareY = 0;
  let cross = 0;
  let momentX = 0;
  let momentY = 0;
  let momentZ = 0;
  for (const point of points) {
    const horizontal = (point.x - mean.x) / scale;
    const vertical = (point.y - mean.y) / scale;
    const squared = horizontal * horizontal + vertical * vertical;
    squareX += (horizontal * horizontal) / points.length;
    squareY += (vertical * vertical) / points.length;
    cross += (horizontal * vertical) / points.length;
    momentX += (horizontal * squared) / points.length;
    momentY += (vertical * squared) / points.length;
    momentZ += (squared * squared) / points.length;
  }
  const meanZ = squareX + squareY;
  const covariance = squareX * squareY - cross * cross;
  if (covariance <= CYLINDER_FIT_CONFIG.numericalFloor) return;
  const varianceZ = momentZ - meanZ * meanZ;
  const cubic = 4 * meanZ;
  const quadratic = -(3 * meanZ * meanZ + momentZ);
  const linear =
    varianceZ * meanZ +
    4 * covariance * meanZ -
    momentX * momentX -
    momentY * momentY;
  const constant =
    momentX * (momentX * squareY - momentY * cross) +
    momentY * (momentY * squareX - momentX * cross) -
    varianceZ * covariance;
  let root = 0;
  for (
    let iteration = 0;
    iteration < CYLINDER_FIT_CONFIG.maximumIterations;
    iteration++
  ) {
    const value =
      constant + root * (linear + root * (quadratic + root * cubic));
    const derivative = linear + root * (2 * quadratic + root * 3 * cubic);
    if (Math.abs(derivative) < CYLINDER_FIT_CONFIG.numericalFloor) break;
    const next = root - value / derivative;
    if (!Number.isFinite(next) || next < 0) break;
    if (Math.abs(next - root) < CYLINDER_FIT_CONFIG.solverTolerance) {
      root = next;
      break;
    }
    root = next;
  }
  const determinant = root * root - root * meanZ + covariance;
  if (Math.abs(determinant) <= CYLINDER_FIT_CONFIG.numericalFloor) return;
  const center = new Vector2(
    (momentX * (squareY - root) - momentY * cross) / (2 * determinant),
    (momentY * (squareX - root) - momentX * cross) / (2 * determinant),
  );
  const radius = Math.sqrt(center.lengthSq() + meanZ) * scale;
  if (!Number.isFinite(radius) || radius <= 0) return;
  return { center: center.multiplyScalar(scale).add(mean), radius };
}

/**
 * @param points Observed cross-section inliers.
 * @returns Taubin initialization followed by orthogonal-distance circle fitting.
 */
export function fitCircleGeometric(
  points: readonly Vector2[],
): CircleFit | undefined {
  const initial = fitCircleTaubin(points);
  if (!initial) return;
  const normalized = points.map((point) =>
    point.clone().sub(initial.center).divideScalar(initial.radius),
  );
  const result = levenbergMarquardt(
    { x: points.map((_, index) => index), y: points.map(() => 0) },
    ([horizontal, vertical, radius]) =>
      (index) =>
        Math.hypot(
          normalized[index].x - horizontal,
          normalized[index].y - vertical,
        ) - radius,
    {
      initialValues: [0, 0, 1],
      minValues: [
        -Infinity,
        -Infinity,
        CYLINDER_FIT_CONFIG.minimumRelativeRadius,
      ],
      jacobianFunction:
        ([horizontal, vertical]) =>
        (index) => {
          const offsetX = normalized[index].x - horizontal;
          const offsetY = normalized[index].y - vertical;
          const distance = Math.max(
            CYLINDER_FIT_CONFIG.numericalFloor,
            Math.hypot(offsetX, offsetY),
          );
          return [-offsetX / distance, -offsetY / distance, -1];
        },
      errorTolerance: CYLINDER_FIT_CONFIG.solverTolerance,
      maxIterations: CYLINDER_FIT_CONFIG.maximumIterations,
      damping: CYLINDER_FIT_CONFIG.damping,
    },
  );
  const [horizontal, vertical, radius] = result.parameterValues;
  if (![horizontal, vertical, radius].every(Number.isFinite) || radius <= 0) {
    return;
  }
  return {
    center: initial.center
      .clone()
      .add(new Vector2(horizontal, vertical).multiplyScalar(initial.radius)),
    radius: radius * initial.radius,
  };
}
